export {}

declare global {
	interface Array<T> {
		toDictionary(key: keyof T): { [key: string]: T };
		toDictionaryMap(keyFunction: (item: T) => string): { [key: string]: T };
		toDictionaryMapValue<TV>(keyFunction: (item: T) => string, valueFunction: (item: T) => TV): { [key: string]: TV };

		groupBy(key: keyof T): { [key: string]: T[] };

		remove(removeItem: T): void;
		removeWhere(removeFunction: (item: T) => boolean): void;
		firstOrDefault(where: (item: T) => boolean): T;

		/**
		 * Returns last item in array or null if the array is empty
		 */
		lastOrNull(): T | null;

		/*
		* Sorts array by key (Optionally descending)
		*/
		sortBy(key: keyof T, desc?: boolean, emptyLast?: boolean): T[];

		/*
		* Sorts array by value property on key object (Optionally descending)
		*/
		sortByValue(key: keyof T, desc?: boolean): T[];

		/*
		* Sorts array by key (Optionally descending)
		*/
		sortByMap<TV>(valueFunction: (item: T) => TV, desc?: boolean): T[];

		/*
		* Sorts array by key (Optionally descending)
		*/
		sortByThen({ key, desc }: { key: keyof T, desc?: boolean },
			{ thenKey, thenDesc }: { thenKey: keyof T, thenDesc?: boolean }): T[];

		/*
		* Creates new array with only distinct members of other array
		*/
		distinct(key?: keyof T | null): T[];

		/**
		 * Find item and pull member
		 * @param predicate Predicate to find item
		 * @param get Function to pull value
		 */
		findAndGet<TV>(predicate: (item: T) => boolean, get: (item: T) => TV, defaultValue: TV): TV;

		/**
		 * Finds maximum value of @key in array. Returns null if no items are present.
		 * @param key Key of value
		 */
		max(key?: keyof T | null): number | null

		/**
		 * Finds maximum value from an array based on given predicate. Returns null if no items are present.
		 * @param predicate mapping of item to value
		 */
		maxMap(predicate: (item: T) => number): number | null

		/**
		 * Adds or updates item in array
		 * @param item item to add or update
		 * @param key key to match existing items
		 */
		upsert(item: T, key?: keyof T): void

		/**
		 * Finds total values of @key in array. Returns 0 if no items are present.
		 * @param key Key of value
		 */
		sum(key?: keyof T | null): number
	}
}

if (!Array.prototype.toDictionary) {
	Array.prototype.toDictionary = function toDictionary<T> (key: keyof T) {
		const dictionary: any = {}
		for (let i = 0; i < this.length; i++) {
			const keyValue = this[i][key]
			dictionary[keyValue] = this[i]
		}
		return dictionary as { [key: string]: T }
	}
}

if (!Array.prototype.toDictionaryMap) {
	Array.prototype.toDictionaryMap = function toDictionaryMap<T> (keyFunction: (item: T) => string): { [key: string]: T } {
		return this.toDictionaryMapValue(keyFunction, v => v)
	}

	Array.prototype.toDictionaryMapValue = function toDictionaryMap<T, TV> (keyFunction: (item: T) => string, valueFunction: (item: T) => TV): { [key: string]: TV } {
		const dictionary: any = {}
		for (let i = 0; i < this.length; i++) {
			const keyValue = keyFunction(this[i])
			dictionary[keyValue] = valueFunction(this[i])
		}
		return dictionary as { [key: string]: TV }
	}
}

if (!Array.prototype.groupBy) {
	Array.prototype.groupBy = function groupBy<T> (key: keyof T) {
		const grouped: { [key: string]: T[] } = {}
		for (let i = 0; i < this.length; i++) {
			const keyValue = this[i][key] ?? ""

			if (Array.isArray(keyValue)) {
				if (keyValue.length > 0) {
					keyValue.forEach(v => {
						if (grouped[v]) {
							grouped[v].push(this[i])
						} else {
							grouped[v] = [this[i]]
						}
					})
				} else {
					if (grouped[""]) {
						grouped[""].push(this[i])
					} else {
						grouped[""] = [this[i]]
					}
				}
			} else {
				if (grouped[keyValue]) {
					grouped[keyValue].push(this[i])
				} else {
					grouped[keyValue] = [this[i]]
				}
			}
		}
		return grouped
	}
}

if (!Array.prototype.sortBy) {
	Array.prototype.sortBy = function sortBy<T> (this: T[], key: keyof T, desc: boolean = false, emptyLast: boolean = false): T[] {
		return this.sort((a: T, b: T) => {
			const aValue = a[key] as any
			const bValue = b[key] as any

			if (typeof aValue === "string" && typeof bValue === "string") {
				if (emptyLast) {
					if (!aValue) {
						if (bValue) {
							return 1
						}
					} else if (!bValue) {
						return -1
					}
				}

				const compare = aValue.localeCompare(bValue)
				return desc ? compare * -1 : compare
			} else if (typeof aValue === "string") {
				return desc ? -1 : 1
			} else if (typeof bValue === "string") {
				return desc ? 1 : -1
			} else {
				if (desc) {
					return bValue - aValue
				} else {
					return aValue - bValue
				}
			}
		})
	}
}

if (!Array.prototype.sortByValue) {
	Array.prototype.sortByValue = function sortBy<T> (this: T[], key: keyof T, desc: boolean = false): T[] {
		return this.sort((a: T, b: T) => {
			let aValue = (a[key] as any).value
			let bValue = (b[key] as any).value

			if (typeof (aValue) === "object" && !!(a[key] as any)) {
				let label = null

				if ("label" in (a[key] as any) && (a[key] as any).label) {
					label = (a[key] as any).label
				} else if (!!aValue && "name" in aValue && !!aValue.name) {
					label = aValue.name
				}

				if (!label || typeof (label) === "string") {
					aValue = label
				} else if (typeof (label) === "object") {
					if (label.name) {
						aValue = label.name
					}
				}
			}

			if (typeof (bValue) === "object" && !!(b[key] as any)) {
				let label = null

				if ("label" in (b[key] as any) && (b[key] as any).label) {
					label = (b[key] as any).label
				} else if (!!bValue && "name" in bValue && !!bValue.name) {
					label = bValue.name
				}

				if (!label || typeof (label) === "string") {
					bValue = label
				} else if (typeof (label) === "object") {
					if (label.name) {
						bValue = label.name
					}
				}
			}

			if (typeof aValue === "string" && typeof bValue === "string") {
				const compare = aValue.localeCompare(bValue)
				return desc ? compare * -1 : compare
			} else if (typeof aValue === "string") {
				return desc ? -1 : 1
			} else if (typeof bValue === "string") {
				return desc ? 1 : -1
			} else {
				if (desc) {
					return bValue - aValue
				} else {
					return aValue - bValue
				}
			}
		})
	}
}

if (!Array.prototype.sortByMap) {
	Array.prototype.sortByMap = function sortByMap<T, TV> (this: T[], valueFunction: (item: T) => TV, desc?: boolean): T[] {
		return this.sort((a: T, b: T) => {
			const aValue = valueFunction(a) as any
			const bValue = valueFunction(b) as any

			if (typeof aValue === "string" && typeof bValue === "string") {
				const compare = aValue.localeCompare(bValue)
				return desc ? compare * -1 : compare
			} else if (typeof aValue === "string") {
				return desc ? -1 : 1
			} else if (typeof bValue === "string") {
				return desc ? 1 : -1
			} else {
				if (desc) {
					return bValue - aValue
				} else {
					return aValue - bValue
				}
			}
		})
	}
}

if (!Array.prototype.sortByThen) {
	Array.prototype.sortByThen = function sortByThen<T> (this: T[], { key, desc }: { key: keyof T, desc?: boolean },
		{ thenKey, thenDesc }: { thenKey: keyof T, thenDesc?: boolean }): T[] {
		return this.sort((a: T, b: T) => {
			const aValue = a[key] as any
			const bValue = b[key] as any

			if (typeof a[key] === "string") {
				let compare = aValue.localeCompare(bValue)

				if (compare === 0) {
					const aValueThen = a[thenKey] as any
					const bValueThen = b[thenKey] as any

					compare = aValueThen.localeCompare(bValueThen)
				}

				return desc ? compare * -1 : compare
			} else {
				let compare = aValue - bValue

				if (aValue === bValue) {
					const aValueThen = a[thenKey] as any
					const bValueThen = b[thenKey] as any

					compare = aValueThen - bValueThen
				}

				return desc ? compare * -1 : compare
			}
		})
	}
}

if (!Array.prototype.distinct) {
	Array.prototype.distinct = function distinct<T> (this: T[], key: keyof T | null = null): T[] {
		const newArray = []
		const addedValues = new Set()

		var i = 0
		if (key) {
			while (i < this.length) {
				if (!addedValues.has(this[i][key])) {
					addedValues.add(this[i][key])
					newArray.push(this[i])
				}
				i++
			}
		} else {
			while (i < this.length) {
				if (!addedValues.has(this[i])) {
					addedValues.add(this[i])
					newArray.push(this[i])
				}
				i++
			}
		}

		return newArray
	}
}

if (!Array.prototype.remove) {
	Array.prototype.remove = function remove<T> (this: T[], removeItem: T): void {
		const removeIndex = this.indexOf(removeItem)
		if (removeIndex > -1) {
			this.splice(removeIndex, 1)
		}
	}
}

if (!Array.prototype.removeWhere) {
	Array.prototype.removeWhere = function removeWhere<T> (this: T[], removeFunction: (item: T) => boolean): void {
		var i = 0
		while (i < this.length) {
			if (removeFunction(this[i])) {
				this.splice(i, 1)
			} else {
				++i
			}
		}
	}
}

if (!Array.prototype.firstOrDefault) {
	Array.prototype.firstOrDefault = function T<T> (this: T[], where: (item: T) => boolean): T | null {
		if (where) {
			var i = 0
			while (i < this.length) {
				if (where(this[i])) {
					return this[i]
				}
				i++
			}
			return null
		} else {
			return this.length > 0 ? this[0] : null
		}
	}
}

if (!Array.prototype.findAndGet) {
	Array.prototype.findAndGet = function <T, TV> (this: T[], predicate: (item: T) => boolean, get: (item: T) => TV, defaultValue: TV): TV {
		const item = this.find(predicate)
		return item ? get(item) : defaultValue
	}
}

if (!Array.prototype.max) {
	Array.prototype.max = function max<T> (this: T[], key: keyof T): number | null {
		let max: number | null = null
		var i = 0
		while (i < this.length) {
			const v = (this[i][key] as any) as number | null
			if (v !== null && v !== undefined) {
				if (max === null || max < v) {
					max = v
				}
			}
			i++
		}

		return max
	}
}

if (!Array.prototype.maxMap) {
	Array.prototype.maxMap = function max<T> (this: T[], predicate: (item: T) => number): number | null {
		let max: number | null = null
		var i = 0
		while (i < this.length) {
			const v = predicate(this[i])
			if (v !== null && v !== undefined) {
				if (max === null || max < v) {
					max = v
				}
			}
			i++
		}
		return max
	}
}

if (!Array.prototype.upsert) {
	Array.prototype.upsert = function upsert<T> (this: T[], item: T, key: keyof T): void {
		const keyValue = item[key]
		if (keyValue) {
			const existingItem = this.find(r => r[key] === keyValue)
			if (existingItem) {
				const keys = [...Object.keys(existingItem), ...Object.keys(item as unknown as object)].distinct()
				keys.forEach(k => {
					const itemKey = k as keyof T
					(existingItem as T)[itemKey] = item[itemKey]
				})
			} else {
				this.push(item)
			}
		} else {
			this.push(item)
		}
	}
}

if (!Array.prototype.sum) {
	Array.prototype.sum = function sum<T> (this: T[], key: keyof T): number {
		let total: number = 0
		var i = 0
		while (i < this.length) {
			const v = (this[i][key] as any) as number | null
			if (v) {
				total += v
			}
			i++
		}

		return total
	}
}

if (!Array.prototype.lastOrNull) {
	Array.prototype.lastOrNull = function last<T> (this: T[]): T | null {
		return this.length > 0 ? this[this.length - 1] : null
	}
}
